/**
 * API годового планирования — карточка 03 (AK-434, PR #563 backend).
 *
 * Слой над /api/v1/planning/* + /api/v1/company/season-config:
 *   - настройка сезона + гейт раздела (`is_configured`);
 *   - список сезонов карточками (агрегат «N из M хозяйств», готовность
 *     бюджетных справочников, дедлайн защиты).
 *
 * UI-язык агронома: «Планируется / Запланирован / Пересмотрен / Завершён /
 * Архив» — модельные термины (planning/planned/…) наружу не выходят.
 */
import { api } from '@/lib/api';
import {
  computeSeasonStatus,
  type SeasonComputedStatus,
} from '@/lib/season/computedStatus';

// ── Настройка сезона (экран 11) ──────────────────────────────────────────────

export interface SeasonConfig {
  company_id: string;
  boundary_type: 'calendar' | 'agro_year' | 'custom';
  custom_start_month: number | null;
  custom_start_day: number | null;
  label_format: 'year' | 'year_span';
  year_semantics: 'harvest' | 'sowing';
  resolver_mode: 'hybrid' | 'manual' | 'date';
  name_template: string | null;
  visible_past_years: number;
  visible_future_years: number;
  planning_opens_offset_days: number | null;
  planning_closes_offset_days: number | null;
  configured_at: string | null;
  configured_by_user_id: string | null;
  is_configured: boolean;
  /**
   * Сколько строк `seasons` реально изменил БД-триггер `trg_season_config_resync`
   * после этого PUT (AK-539, #664). Три состояния:
   *
   *   - `null`   — правило границ не меняли (например, поменяли только окно
   *                видимости); пересчёта не было. Блок «Пересчитано» скрыт.
   *   - `0`      — правило изменилось, но подписи и границы совпали (напр.
   *                `name_template` дал те же строки). Честно сказать
   *                «границы не изменились» — это НЕ ошибка.
   *   - `N > 0`  — реально пересчитано N сезонов. Показываем счётчик +
   *                кнопку «посмотреть новые границы» → /planning.
   *
   * Считается диффом снимка (starts_on, ends_on, name) до/после записи в
   * БД-триггере, а не через COUNT(*) — прежняя реализация возвращала 51.
   */
  seasons_recalculated: number | null;
}

/**
 * Поля `season_config`, смена которых на бэке считается сменой правила
 * календаря и запускает `trg_season_config_resync`. Держим синхронно с
 * `_SEASON_RULE_FIELDS` из `backend/src/ikolhoz/api/v1/season_planning.py`
 * (AK-539). Используется в UI для подсказок «эта правка пересчитает сезоны».
 */
export const SEASON_RULE_FIELDS = [
  'boundary_type',
  'custom_start_month',
  'custom_start_day',
  'year_semantics',
  'label_format',
  'name_template',
] as const;

export type SeasonRuleField = (typeof SEASON_RULE_FIELDS)[number];

export type SeasonConfigPatch = Partial<
  Pick<
    SeasonConfig,
    | 'boundary_type'
    | 'custom_start_month'
    | 'custom_start_day'
    | 'label_format'
    | 'name_template'
    | 'visible_past_years'
    | 'visible_future_years'
    | 'planning_opens_offset_days'
    | 'planning_closes_offset_days'
  >
>;

/**
 * Настройка сезона одной компании. `company_id` пробрасывается только когда
 * бэк вернул 409 `company_ambiguous` (в холдинге несколько компаний) и юзер
 * выбрал одну — иначе бэк сам находит единственную компанию тенанта.
 */
export function getSeasonConfig(companyId?: string | null): Promise<SeasonConfig> {
  const qs = companyId ? `?company_id=${encodeURIComponent(companyId)}` : '';
  return api.get<SeasonConfig>(`/api/v1/company/season-config${qs}`);
}

export function saveSeasonConfig(
  patch: SeasonConfigPatch,
  companyId?: string | null,
): Promise<SeasonConfig> {
  const qs = companyId ? `?company_id=${encodeURIComponent(companyId)}` : '';
  return api.put<SeasonConfig>(`/api/v1/company/season-config${qs}`, patch);
}

/**
 * Классификация штатных 4xx с бэка настройки сезона в UI-состояния (SA-03 §UX,
 * доработка AK-541 после аудита пустого тенанта). Возвращает узкий литерал —
 * компонент разветвляется по нему без string-matching по тексту ошибки.
 *
 * Различаем ровно три штатных случая (см. `season_planning.py::resolve_company_id`):
 * - `no_company` → тенант пустой, компанию ещё не создали;
 * - `company_ambiguous` → в холдинге несколько компаний, нужен выбор;
 * - `other` → всё остальное (сеть, 5xx, 401/403), поведение как раньше.
 *
 * Диагностика — по `status + rawDetail.code`, не по тексту сообщения:
 * `HTTPException(detail=str)` даёт `rawDetail=string`, а
 * `HTTPException(detail={code:...})` — `rawDetail={code:...}`. Такой контракт
 * ловит только явно объявленные коды бэка, а не «В тенанте нет компаний».
 */
export type SeasonConfigLoadKind = 'no_company' | 'company_ambiguous' | 'other';

export function classifySeasonConfigError(err: unknown): SeasonConfigLoadKind {
  const e = err as { status?: number; rawDetail?: unknown; detail?: string } | null;
  if (!e || typeof e.status !== 'number') return 'other';
  const raw = e.rawDetail;
  const code =
    raw && typeof raw === 'object' && 'code' in raw && typeof (raw as { code: unknown }).code === 'string'
      ? ((raw as { code: string }).code)
      : null;
  if (e.status === 404 && (code === 'no_company' || typeof raw === 'string')) {
    // 404 с телом-строкой = «В тенанте нет компаний» (сегодня backend отдаёт
    // именно так — HTTPException(status_code=404, detail="…")); в будущем
    // может уехать на структурированный `code`, поэтому ловим оба варианта.
    return 'no_company';
  }
  if (e.status === 409 && code === 'company_ambiguous') return 'company_ambiguous';
  return 'other';
}

// ── Список сезонов (экран 10) ────────────────────────────────────────────────

export type SeasonPlanStatus =
  | 'planning'
  | 'planned'
  | 'revised'
  | 'completed'
  | 'archived';

export interface SeasonCard {
  /**
   * ADR-060 §S6 — список сезонов ВИРТУАЛЬНЫЙ: годы порождает правило календаря,
   * а строка `seasons` появляется только с первой записью в сезоне. Поэтому
   * `season_id` НУЛЛЕВОЙ: `null` = год в окне, в котором ещё ничего не делали.
   *
   * До AK-648 фронт объявлял поле обязательным (`string`) и верил в это на всех
   * экранах бюджета: на плановом сезоне (2027, записей нет) `season_id`
   * приезжал `null`, ветка «сезон не найден» не срабатывала (карточка-то есть),
   * и страницы «Бюджетные справочники» / «Техкарты» рисовали ПУСТОТУ под
   * шапкой, а «Наполнить справочники» молча не открывала мастер.
   */
  season_id: string | null;
  /** Строка сезона существует (бюджет сезона уже можно вести). */
  is_materialized: boolean;
  year: number;
  code: string | null;
  name: string | null;
  starts_on: string | null;
  ends_on: string | null;
  is_current: boolean;
  farms_total: number;
  farms_planned: number;
  status: SeasonPlanStatus;
  status_title: string;
  catalogs_approved: number;
  catalogs_total: number;
  scenarios_count: number;
  plan_due_on: string | null;
}

/**
 * `company_id` пробрасывается симметрично `getSeasonConfig`: без него бэк
 * `GET /planning/seasons` уходит через тот же `resolve_company_id` и в
 * холдинге с несколькими компаниями отдаёт 409 `company_ambiguous` — юзер
 * получал «Не удалось загрузить сезоны. Обновите страницу.» уже после того,
 * как выбрал компанию для конфига (P1-3 head-web).
 */
export function listSeasonCards(showAll = false, companyId?: string | null): Promise<SeasonCard[]> {
  const p = new URLSearchParams();
  if (showAll) p.set('show_all', 'true');
  if (companyId) p.set('company_id', companyId);
  const qs = p.toString();
  return api.get<SeasonCard[]>(`/api/v1/planning/seasons${qs ? `?${qs}` : ''}`);
}

// ── Открыть бюджет сезона у хозяйства ────────────────────────────────────────

export interface SeasonOpenResult {
  season_id: string;
  year: number;
  /** Сезон уже существовал строкой до вызова. */
  was_materialized: boolean;
  /** Сколько хозяйств теперь в цикле планирования сезона. */
  farms_opened: number;
}

/**
 * «Открыть бюджет сезона {год}» — единственное действие, которым бюджет
 * сезона входит в работу (AK-648).
 *
 * Адрес — ГОД, а не `season_id`, и это принципиально: у сезона, в котором ещё
 * ничего не делали, строки нет вовсе (ADR-060 §S6, `season_id: null`), звать
 * `POST /seasons/{id}/plan/open` нечем. Ручка сама материализует сезон
 * (`ensure_season`) и открывает план хозяйствам в доступе. Идемпотентна —
 * повторное нажатие безопасно.
 *
 * `farmId` — открыть только одному хозяйству; по умолчанию открываем всем в
 * доступе, потому что «открыть бюджет сезона» пользователь понимает как
 * действие уровня компании.
 */
export function openSeasonBudget(
  year: number,
  opts: { farmId?: string | null; companyId?: string | null } = {},
): Promise<SeasonOpenResult> {
  return api.post<SeasonOpenResult>(`/api/v1/planning/seasons/${year}/open-budget`, {
    farm_id: opts.farmId ?? null,
    company_id: opts.companyId ?? null,
  });
}

// ── Статусы БЮДЖЕТА сезона: подписи и вид бейджа ─────────────────────────────
//
// Канон v3 §6: это лестница бюджета (`season_plans`), а НЕ статус сезона.
// Статус сезона вычисляется отдельно — см. `seasonCardStatus` ниже и
// `@/lib/season/computedStatus`. На экранах они стоят разными колонками:
// «Статус сезона» (вычисляемый) и «Бюджет сезона» (эта лестница).

export const PLAN_STATUS_TITLES: Record<SeasonPlanStatus, string> = {
  planning: 'Планируется',
  planned: 'Запланирован',
  revised: 'Пересмотрен',
  completed: 'Завершён',
  archived: 'Архив',
};

/** Вариант Badge под статус — только semantic-варианты DS. */
export const PLAN_STATUS_BADGE: Record<
  SeasonPlanStatus,
  'default' | 'primary' | 'accent-brand' | 'outline'
> = {
  planning: 'default',
  planned: 'primary',
  revised: 'accent-brand',
  completed: 'outline',
  archived: 'outline',
};

// ── Хаб планирования (экран 00) ──────────────────────────────────────────────

// ── Статус СЕЗОНА (вычисляемый, канон v3) поверх статуса БЮДЖЕТА ─────────────

/**
 * Бюджет сезона утверждён? Вход вычислителя статуса сезона (канон v3 §5
 * «Запланирован — бюджет утверждён»).
 *
 * `planned`/`revised` — рабочее множество утверждённого бюджета (зеркало
 * `APPROVED_PLAN_STATUSES` в `services/season_planning.py`). `completed`/
 * `archived` добавлены как «бюджет когда-то был утверждён» — на статус сезона
 * они всё равно не влияют (у таких сезонов даты рамки давно прошли, и
 * побеждает «Завершён»), но и врать «Не запланирован» про них не нужно.
 */
export function isSeasonBudgetApproved(card: Pick<SeasonCard, 'status'>): boolean {
  return (
    card.status === 'planned' ||
    card.status === 'revised' ||
    card.status === 'completed' ||
    card.status === 'archived'
  );
}

/**
 * Статус сезона карточки — по датам рамки + утверждённости бюджета
 * (канон v3 §5). Даты — основной путь: `computeSeasonStatus` даёт точный
 * ответ, если `starts_on`/`ends_on` пришли. Без конца рамки вычислитель сам
 * подстраховывается годом сезона (`SeasonStatusInput.year`, п.3 в шапке
 * `computedStatus.ts`) — без этого прошедший сезон без рамки получил бы
 * «Запланирован» вместо «Завершён» (находка 1, вердикт head-web на #699).
 *
 * У карточки бюджет известен всегда, поэтому `null` вернуться не может —
 * сужаем тип для вызывающих.
 */
export function seasonCardStatus(
  card: SeasonCard,
  today?: Date | string,
): SeasonComputedStatus {
  return (
    computeSeasonStatus(
      {
        startsOn: card.starts_on,
        endsOn: card.ends_on,
        year: card.year,
        isCurrent: card.is_current,
        budgetApproved: isSeasonBudgetApproved(card),
      },
      today,
    ) ?? 'not_planned'
  );
}

/**
 * Три жизненные фазы сезона — как формулирует founder (05.08.2026, story #6):
 * «AgroHistory → завершённые (2018-2025), 2026 = идущий, 2027 = плановый».
 * UI-язык: «Завершённые / Идущий / Плановый».
 */
export type SeasonPhase = 'past' | 'current' | 'future';

export const SEASON_PHASE_TITLES: Record<SeasonPhase, string> = {
  past: 'Завершённые',
  current: 'Идущий',
  future: 'Плановый',
};

/**
 * Разложить карточки в 3 ведра по ВЫЧИСЛЯЕМОМУ статусу сезона
 * (канон v3 §5, `seasonCardStatus`), а НЕ по `season_plans.status`:
 *   - `past`    → «Завершён» (даты рамки прошли, либо год < текущего при
 *     отсутствии рамки — см. фолбэк в `seasonCardStatus`);
 *   - `current` → «Идёт» (сегодня внутри рамки / канон пометил текущим);
 *   - `future`  → всё остальное («Запланирован» / «Не запланирован»).
 *
 * До вычисляемого статуса ведро выбирал статус БЮДЖЕТА: сезоны 2018-2025,
 * у которых никто не заводил `season_plans`, приходили со статусом
 * `planning` и падали в «Плановый» — хаб утверждал, что прошлые сезоны
 * сейчас планируются (post-mortem revert #690).
 *
 * `today` — `Date`/ISO-строка для тестируемости (unit'ы фиксируют день,
 * UI использует системную дату по умолчанию).
 *
 * Порядок внутри ведра:
 *   - past    → по году убывающе (свежие сверху)
 *   - current → по году (обычно ровно один)
 *   - future  → по году возрастающе (ближайший сверху)
 */
export function groupSeasonsByPhase(
  cards: SeasonCard[],
  today?: Date | string,
): Record<SeasonPhase, SeasonCard[]> {
  const past: SeasonCard[] = [];
  const current: SeasonCard[] = [];
  const future: SeasonCard[] = [];
  for (const c of cards) {
    const status = seasonCardStatus(c, today);
    if (status === 'completed') {
      past.push(c);
    } else if (status === 'active') {
      current.push(c);
    } else {
      future.push(c);
    }
  }
  past.sort((a, b) => b.year - a.year);
  current.sort((a, b) => b.year - a.year);
  future.sort((a, b) => a.year - b.year);
  return { past, current, future };
}

// ── Хелперы представления ────────────────────────────────────────────────────

/**
 * Подпись агрегата «программы утвердили N из M хозяйств».
 * При одном хозяйстве множественное число не нужно.
 */
export function farmsAggregateLabel(card: SeasonCard): string {
  if (card.farms_total <= 0) return 'нет хозяйств в доступе';
  if (card.farms_total === 1) {
    return card.farms_planned >= 1
      ? 'программа хозяйства утверждена'
      : 'программа хозяйства не утверждена';
  }
  return `программы утвердили ${card.farms_planned} из ${card.farms_total} хозяйств`;
}

/**
 * «3 из 5 бюджетных справочников утверждены» для строки карточки.
 * Канон v3: `season_catalog_status` → UI-термин «Бюджетные справочники».
 */
export function catalogsReadinessLabel(card: SeasonCard): string {
  return `бюджетные справочники: ${card.catalogs_approved} из ${card.catalogs_total} утверждены`;
}

/**
 * Дедлайн защиты плана — human-формат или null, если не задан.
 * Дата приходит ISO (plan_due_on из season_config-окна).
 */
export function planDueLabel(card: SeasonCard): string | null {
  if (!card.plan_due_on) return null;
  const d = new Date(`${card.plan_due_on}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return `дедлайн защиты ${d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}
